import React, { useState, useEffect } from 'react';
import { Story } from '../types';
import { downloadAudioAsWav, getAudioBlob } from '../services/geminiService';
import { shareStory } from '../services/storageService';

interface StoryDisplayProps {
  story: Story;
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  isPublic?: boolean;
  onToggleAudio: () => void;
  onBack: () => void;
}

const StoryDisplay: React.FC<StoryDisplayProps> = ({ story, isPlaying, isPaused, isLoading, isPublic = false, onToggleAudio, onBack }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);

  // Pre-fetch audio as soon as the story is displayed
  useEffect(() => {
    if (story.audioBase64) {
      console.log("Pre-fetching audio for faster playback...");
      getAudioBlob(story.audioBase64).catch(err => {
        console.warn("Audio pre-fetch failed, will retry on play:", err);
      });
    }
  }, [story.audioBase64]);

  const handlePrint = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setTimeout(() => { window.print(); }, 200);
  };

  const handleShare = async () => {
    try {
      setIsSharing(true);
      const storyId = await shareStory(story);
      const shareUrl = `${window.location.origin}${window.location.pathname}?story=${storyId}`;
      
      if (navigator.share) {
        await navigator.share({
          title: `Përralla: ${story.title}`,
          text: `Shiko këtë përrallë magjike të krijuar me PërrallAIme!`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShowShareSuccess(true);
        setTimeout(() => setShowShareSuccess(false), 3000);
      }
    } catch (err) {
      console.error("Gabim gjatë shpërndarjes:", err);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="space-y-10 md:space-y-16 lg:space-y-20 animate-in fade-in duration-700 pb-24 max-w-4xl mx-auto">
      <div className="text-center space-y-6 pt-4 no-print px-4">
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-fairytale tracking-tight leading-tight text-glow-yellow">{story.title}</h2>
        
        <div className="flex flex-col md:flex-row items-center justify-center gap-3 no-print px-4">
          <button
            type="button"
            onClick={onToggleAudio}
            disabled={!story.audioBase64 || isLoading}
            className={`flex items-center justify-center gap-2 px-6 py-3 md:px-10 md:py-4 rounded-full font-bold shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 whitespace-nowrap w-full md:w-auto min-w-[240px] md:min-w-[360px] ${
              isPlaying && !isPaused
                ? 'bg-orange-500 text-white shadow-orange-500/20' 
                : isPlaying && isPaused
                  ? 'bg-green-500 text-white shadow-green-500/20'
                  : isLoading
                    ? 'bg-indigo-400 text-white shadow-indigo-400/20 cursor-wait'
                    : 'bg-yellow-400 text-indigo-900 shadow-yellow-400/20'
            }`}
          >
            <span className="flex items-center gap-2 text-sm md:text-lg">
              {isLoading ? (
                <>
                  <span className="w-4 h-4 md:w-5 md:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  <span>Duke u ngarkuar...</span>
                </>
              ) : isPlaying ? (
                isPaused ? (
                  <>
                    <span className="text-xl md:text-2xl">▶️</span> Vazhdo Leximin
                  </>
                ) : (
                  <>
                    <span className="text-xl md:text-2xl">⏸️</span> Pushim
                  </>
                )
              ) : (
                <>
                  <span className="text-xl md:text-2xl">🔊</span> Dëgjo Përrallën
                </>
              )}
            </span>
          </button>

          <div className="flex items-center gap-2 w-full md:w-auto justify-center">
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 px-4 py-3 md:px-6 md:py-4 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all shadow-sm active:scale-95 group whitespace-nowrap flex-1 md:flex-none"
              title="Printo Përrallën"
            >
              <span className="text-lg md:text-xl group-hover:-translate-y-0.5 transition-transform">🖨️</span>
              <span className="text-xs md:text-sm font-bold uppercase tracking-wider">Printo</span>
            </button>

            {!isPublic && (
              <button
                type="button"
                onClick={handleShare}
                disabled={isSharing}
                className={`flex items-center justify-center gap-2 px-4 py-3 md:px-6 md:py-4 rounded-full border transition-all shadow-sm active:scale-95 group whitespace-nowrap flex-1 md:flex-none ${
                  showShareSuccess 
                    ? 'bg-green-500/20 border-green-500/50 text-green-400' 
                    : 'bg-indigo-500/20 border-indigo-400/30 text-indigo-100 hover:bg-indigo-500/30'
                }`}
                title="Shpërndaj Përrallën"
              >
                <span className={`text-lg md:text-xl transition-transform ${isSharing ? 'animate-spin' : 'group-hover:rotate-12'}`}>
                  {showShareSuccess ? '✅' : isSharing ? '⏳' : '🔗'}
                </span>
                <span className="text-xs md:text-sm font-bold uppercase tracking-wider">
                  {showShareSuccess ? 'Gati!' : isSharing ? '...' : 'Share'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-12 md:space-y-20 lg:space-y-24 px-4 sm:px-0">
        {(story.parts || []).map((part, index) => {
          // Fallback for older stories that might use 'image' instead of 'imageUrl'
          const imgUrl = part.imageUrl || (part as any).image;
          
          return (
            <div key={index} className="space-y-6 md:space-y-10 story-page">
              <div className="aspect-square glass-card rounded-[2.5rem] overflow-hidden border border-white/10 shadow-2xl relative group">
                {imgUrl ? (
                  <img 
                    src={imgUrl} 
                    alt={`Skena ${index + 1}`} 
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
                    loading="eager"
                  />
                ) : (
                  <div className="w-full h-full bg-white/5 animate-pulse flex items-center justify-center">
                    <span className="text-5xl">✨</span>
                  </div>
                )}
                <div className="absolute top-6 left-6 w-10 h-10 md:w-14 md:h-14 bg-indigo-600/90 backdrop-blur-md rounded-2xl flex items-center justify-center font-black font-comic text-white text-lg md:text-2xl shadow-xl">
                  {index + 1}
                </div>
              </div>
              
              <div className="p-8 md:p-14 lg:p-16 glass-card rounded-[3rem] border border-white/5 shadow-inner relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-indigo-50/5 rounded-full blur-3xl"></div>
                <p className="text-xl md:text-3xl lg:text-4xl leading-[1.6] text-indigo-50 font-medium font-quicksand relative z-10">
                  {part.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center pt-16 no-print">
        <button
          onClick={onBack}
          className="bg-white/5 hover:bg-white/10 text-white px-10 py-4 rounded-full font-bold border border-white/10 transition-all hover:px-12 active:scale-95 flex items-center gap-3"
        >
          <span className="text-xl">{isPublic ? '✨' : '🌙'}</span> {isPublic ? 'Krijo Përrallën Tënde' : 'Kthehu te Libraria'}
        </button>
      </div>
    </div>
  );
};

export default StoryDisplay;